<template>
  <div ref="trigger" class="scroll-trigger" aria-hidden="true">
    <slot />
  </div>
</template>

<script>
const SCROLL_THROTTLE_MS = 200;
const DEFAULT_BOTTOM_MARGIN = 800;
const IGNORED_EMIT_RETRY_MS = 500;

export default {
  name: "ScrollTrigger",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({
        root: null,
        threshold: 0,
        // Pre-fire ~800px before the bottom so pagination has headroom even
        // when the main thread is busy. A 0-height observed rect is a spec
        // edge case across browsers; the rootMargin + min-height below give
        // the observer something concrete to intersect with.
        rootMargin: "0px 0px 800px 0px",
      }),
    },
  },
  emits: ["intersected"],
  data: () => ({
    observer: null,
    scrollRaf: null,
    lastScrollEmit: 0,
    armed: true,
    ignoredEmitTimer: null,
  }),
  computed: {
    bottomMargin() {
      const margin = this.options?.rootMargin;
      if (typeof margin !== "string") {
        return DEFAULT_BOTTOM_MARGIN;
      }

      const parts = margin.trim().split(/\s+/);
      const bottomToken = parts[2] || parts[0] || "0";
      const parsed = parseFloat(bottomToken);
      return Number.isFinite(parsed) ? parsed : DEFAULT_BOTTOM_MARGIN;
    },
  },
  watch: {
    disabled(isDisabled, wasDisabled) {
      if (isDisabled) {
        this.armed = false;
        this.clearIgnoredEmitTimer();
        return;
      }

      if (wasDisabled && !isDisabled) {
        this.rearm();
      }
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry && entry.isIntersecting) {
        this.emitIfNear();
      }
    }, this.options);
    this.observer.observe(this.$refs.trigger);

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.visualViewport?.addEventListener("resize", this.onScroll);
  },
  beforeUnmount() {
    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener("scroll", this.onScroll);
    window.visualViewport?.removeEventListener("resize", this.onScroll);
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    this.clearIgnoredEmitTimer();
  },
  methods: {
    clearIgnoredEmitTimer() {
      if (this.ignoredEmitTimer) {
        clearTimeout(this.ignoredEmitTimer);
        this.ignoredEmitTimer = null;
      }
    },
    isNearViewport() {
      const el = this.$refs.trigger;
      if (!el) {
        return false;
      }

      const rect = el.getBoundingClientRect();
      return rect.top < (window.innerHeight || 0) + this.bottomMargin;
    },
    emitIfNear() {
      if (this.disabled || !this.armed) {
        return;
      }

      if (!this.isNearViewport()) {
        return;
      }

      // One emit per load cycle so iOS/layout re-checks cannot storm fetchMore.
      this.armed = false;
      this.$emit("intersected");
      this.$nextTick(() => {
        if (this.disabled) {
          return;
        }

        this.clearIgnoredEmitTimer();
        this.ignoredEmitTimer = setTimeout(() => {
          this.armed = true;
        }, IGNORED_EMIT_RETRY_MS);
      });
    },
    rearm() {
      const el = this.$refs.trigger;
      if (!this.observer || !el) {
        return;
      }

      // Cycle the observation so iOS Safari re-evaluates after layout-only moves.
      this.observer.unobserve(el);
      this.observer.observe(el);
      this.armed = true;
      this.$nextTick(() => {
        this.emitIfNear();
      });
    },
    onScroll() {
      if (this.disabled || !this.armed) {
        return;
      }

      if (this.scrollRaf) {
        return;
      }

      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        const now = Date.now();
        if (now - this.lastScrollEmit < SCROLL_THROTTLE_MS) {
          return;
        }

        this.lastScrollEmit = now;
        this.emitIfNear();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-trigger {
  display: block;
  width: 100%;
  min-height: 1px;
}
</style>
